import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import immediateOnDemandRealtime from "../../../services/immediateOnDemandRealtime";
import {
  MATCH_RETRY_MS,
  buildMatchingCriteria,
  getAppointmentId,
  getMatchLabel,
} from "./matchingUtils";
import "./immediateOnDemand.css";

// Status values stay in English; the label is translated when it is read.
const STATUS_KEYS = ["connecting", "queued", "waiting", "matched", "pending", "paused", "error"];
const STATUS_LABELS = STATUS_KEYS.reduce((labels, key) => {
  Object.defineProperty(labels, key, {
    enumerable: true,
    get: () => i18n.t(`clientLive:waiting.status.${key}`),
  });
  return labels;
}, {});

const CLIENT_WAIT_MINUTES = 2;
const CLIENT_MAX_WAIT_MINUTES = 6;
const CLIENT_WAIT_OPTION = { label: "2 minutes", value: "2 minutes" };
const PROVIDER_HEARTBEAT_MS = 15000;
const IMMEDIATE_SESSION_TYPE = process.env.REACT_APP_ONDEMAND_SESSION_TYPE || "5";
const IMMEDIATE_SUBSESSION_ID = process.env.REACT_APP_ONDEMAND_SUBSESSION_ID || "";
const IMMEDIATE_DURATION = process.env.REACT_APP_ONDEMAND_DURATION || "";


const getRealtimeErrorMessage = (error) => {
  if (!error) {
    return i18n.t("clientLive:waiting.connectionError");
  }

  if (typeof error === "string") {
    return error;
  }

  if (error.message) {
    return error.message;
  }

  if (error.data && error.data.message) {
    return error.data.message;
  }

  return i18n.t("clientLive:waiting.connectionError");
};

const parseWaitMinutes = (waitTime) => {
  if (!waitTime) {
    return 0;
  }

  const minutes = parseInt(waitTime, 10);
  return Number.isNaN(minutes) ? 0 : minutes;
};

const formatCountdown = (seconds) => {
  const safeSeconds = Math.max(seconds, 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const buildImmediateBookingPayload = () => {
  const booking = {
    sessionType: IMMEDIATE_SESSION_TYPE,
    amount: "0",
    currency: "USD",
  };

  if (IMMEDIATE_SUBSESSION_ID) {
    booking.subsessionId = IMMEDIATE_SUBSESSION_ID;
  }

  if (IMMEDIATE_DURATION) {
    booking.duration = IMMEDIATE_DURATION;
  }

  return booking;
};

export default function ImmediateOnDemandWaiting({
  participantRole,
  settings,
  exitPath,
  matchedPath,
  title,
  onExit,
  onMatched,
  queueSource,
  sourceAppointmentId,
  embedded = false,
  showBookFutureSession = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("clientLive");
  const user = useSelector((state) => state.user.user);
  const criteria = useMemo(() => buildMatchingCriteria(user), [user]);
  const isProvider = participantRole === "provider";
  const selectedWaitTime = settings && settings.waitTime;
  const [status, setStatus] = useState("connecting");
  const [matchData, setMatchData] = useState(null);
  const [message, setMessage] = useState("");
  const initialWaitMinutes = useMemo(
    () => (isProvider ? parseWaitMinutes(selectedWaitTime) : CLIENT_WAIT_MINUTES),
    [isProvider, selectedWaitTime]
  );
  const waitOptions = isProvider
    ? (settings && settings.waitOptions) || []
    : [CLIENT_WAIT_OPTION];
  const [activeWaitMinutes, setActiveWaitMinutes] = useState(initialWaitMinutes);
  const [remainingSeconds, setRemainingSeconds] = useState(initialWaitMinutes * 60);
  const [nextWaitTime, setNextWaitTime] = useState(
    isProvider ? selectedWaitTime || "" : CLIENT_WAIT_OPTION.value
  );
  const [waitExpired, setWaitExpired] = useState(false);
  const [noMatchFound, setNoMatchFound] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const retryTimerRef = useRef(null);
  const totalClientWaitMinutesRef = useRef(0);
  const providerHeartbeatTimerRef = useRef(null);
  const matchedRef = useRef(false);
  const matchingPausedRef = useRef(false);
  const providerInQueueRef = useRef(false);
  const audioRef = useRef(null);
  const audioIntervalRef = useRef(null);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const clearProviderHeartbeat = () => {
    if (providerHeartbeatTimerRef.current) {
      clearInterval(providerHeartbeatTimerRef.current);
      providerHeartbeatTimerRef.current = null;
    }
  };

  const buildProviderQueuePayload = () => ({
    ...criteria,
    ...(queueSource ? { source: queueSource } : {}),
    ...(sourceAppointmentId ? { sourceAppointmentId } : {}),
  });

  const sendProviderHeartbeat = () => {
    if (!isProvider || matchedRef.current || matchingPausedRef.current || !providerInQueueRef.current) {
      return;
    }

    immediateOnDemandRealtime.heartbeatProviderQueue(buildProviderQueuePayload()).catch((error) => {
      const errorMessage = getRealtimeErrorMessage(error);
      clearProviderHeartbeat();
      providerInQueueRef.current = false;
      setStatus("error");
      setMessage(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
      });
    });
  };

  const startProviderHeartbeat = () => {
    clearProviderHeartbeat();
    providerHeartbeatTimerRef.current = setInterval(sendProviderHeartbeat, PROVIDER_HEARTBEAT_MS);
  };

  const completeMatch = (data) => {
    if (matchedRef.current) return;

    matchedRef.current = true;
    clearRetryTimer();
    clearProviderHeartbeat();
    stopRing();
    providerInQueueRef.current = false;
    matchingPausedRef.current = false;
    setWaitExpired(false);
    setStatus("matched");
    setMatchData(data);
    setMessage(t("waiting.appointmentCreated"));

    const appointmentId = getAppointmentId(data);
    localStorage.setItem("immediateOnDemandMatch", JSON.stringify(data || {}));
    if (appointmentId) {
      localStorage.setItem("appoinmentId", appointmentId);
    }

    setTimeout(() => {
      if (onMatched) {
        onMatched(data);
        return;
      }
      navigate(matchedPath(data), { replace: true });
    }, 1200);
  };

  const joinProviderQueue = () => {
    if (matchedRef.current || matchingPausedRef.current) return;
    immediateOnDemandRealtime.joinProviderQueue(buildProviderQueuePayload()).then(() => {
      providerInQueueRef.current = true;
      setStatus("queued");
      startProviderHeartbeat();
    }).catch((error) => {
      const errorMessage = getRealtimeErrorMessage(error);
      clearProviderHeartbeat();
      providerInQueueRef.current = false;
      setStatus("error");
      setMessage(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
      });
    });
  };

  const requestClientMatch = () => {
    if (matchedRef.current || matchingPausedRef.current) return;
    immediateOnDemandRealtime.requestClientMatch({
      ...criteria,
      booking: buildImmediateBookingPayload(),
    }).then((data) => {
      if (data && getAppointmentId(data)) {
        completeMatch(data);
        return;
      }

      if (data) {
        setStatus("waiting");
        setMessage(data.message || STATUS_LABELS.waiting);
      }
    }).catch((error) => {
      const errorMessage = getRealtimeErrorMessage(error);
      setStatus("error");
      setMessage(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
      });
    });
    setStatus((currentStatus) =>
      currentStatus === "matched" ? currentStatus : "waiting"
    );
  };

  const startClientRetry = () => {
    clearRetryTimer();
    requestClientMatch();
    retryTimerRef.current = setInterval(requestClientMatch, MATCH_RETRY_MS);
  };

  const pauseMatchingQueue = () => {
    matchingPausedRef.current = true;
    clearRetryTimer();
    clearProviderHeartbeat();

    if (isProvider && providerInQueueRef.current && !matchedRef.current) {
      immediateOnDemandRealtime.leaveProviderQueue(criteria).catch(() => {});
      providerInQueueRef.current = false;
    }

    setStatus("paused");
    setMessage(STATUS_LABELS.paused);
  };

  useEffect(() => {
    matchingPausedRef.current = false;
    providerInQueueRef.current = false;
    setActiveWaitMinutes(initialWaitMinutes);
    setRemainingSeconds(initialWaitMinutes * 60);
    setNextWaitTime(isProvider ? selectedWaitTime || "" : CLIENT_WAIT_OPTION.value);
    setWaitExpired(false);
    totalClientWaitMinutesRef.current = 0;
    setNoMatchFound(false);
  }, [initialWaitMinutes, isProvider, selectedWaitTime]);

  useEffect(() => {
    if (!activeWaitMinutes || matchedRef.current || status === "matched" || waitExpired) {
      return undefined;
    }

    if (remainingSeconds <= 0) {
      if (!waitExpired) {
        setNextWaitTime(isProvider ? "" : CLIENT_WAIT_OPTION.value);
        pauseMatchingQueue();

        if (!isProvider) {
          const totalWaited = totalClientWaitMinutesRef.current + activeWaitMinutes;
          totalClientWaitMinutesRef.current = totalWaited;
          if (totalWaited >= CLIENT_MAX_WAIT_MINUTES) {
            setNoMatchFound(true);
          }
        }
      }
      setWaitExpired(true);
      ringPlay();
      return undefined;
    }

    const countdownTimer = setInterval(() => {
      setRemainingSeconds((currentSeconds) => Math.max(currentSeconds - 1, 0));
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [activeWaitMinutes, isProvider, remainingSeconds, status, waitExpired]);

  useEffect(() => {
    const handleJoinedQueue = (data) => {
      providerInQueueRef.current = true;
      setStatus("queued");
      setMessage((data && data.message) || STATUS_LABELS.queued);
    };

    const handleWaiting = (data) => {
      if (matchingPausedRef.current) return;
      setStatus("waiting");
      setMessage((data && data.message) || STATUS_LABELS.waiting);
    };

    const handleMatchFound = (data) => {
      completeMatch(data);
    };

    const handleMatchPending = (data) => {
      matchedRef.current = true;
      clearRetryTimer();
      clearProviderHeartbeat();
      providerInQueueRef.current = false;
      matchingPausedRef.current = false;
      setWaitExpired(false);
      setStatus("pending");
      setMessage((data && data.message) || STATUS_LABELS.pending);
      toast.info((data && data.message) || STATUS_LABELS.pending, {
        position: "top-center",
      });
    };

    const handleServiceError = (data) => {
      setStatus("error");
      setMessage((data && data.message) || STATUS_LABELS.error);
      toast.error((data && data.message) || STATUS_LABELS.error, {
        position: "top-center",
      });
    };

    const handleRealtimeError = (data) => {
      const errorMessage = getRealtimeErrorMessage(data);

      setStatus("error");
      setMessage(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
      });
    };

    const unsubscribe = immediateOnDemandRealtime.subscribe(user && user.id, {
      queued: handleJoinedQueue,
      waiting_in_queue: handleWaiting,
      match_found: handleMatchFound,
      match_pending: handleMatchPending,
      match_error: handleServiceError,
      service_unavailable: handleServiceError,
      error: handleRealtimeError,
    });

    if (!matchedRef.current && !matchingPausedRef.current) {
      setStatus("queued");
      setMessage("");

      if (isProvider) {
        joinProviderQueue();
      } else {
        startClientRetry();
      }
    }

    return () => {
      clearRetryTimer();
      clearProviderHeartbeat();

      // Providers have a Redis queue entry; remove it when they leave this page
      // unless a match was already emitted by the realtime service.
      if (isProvider && providerInQueueRef.current && !matchedRef.current) {
        immediateOnDemandRealtime.leaveProviderQueue(criteria).catch(() => {});
        providerInQueueRef.current = false;
      }

      unsubscribe();
      immediateOnDemandRealtime.disconnect();
    };
  }, [criteria, isProvider, matchedPath, navigate, onMatched, user]);

  // Shared teardown for leaving the matching queue: stops polling/heartbeat,
  // stops the ring alarm, leaves the provider queue server-side if needed,
  // and disconnects the realtime channel. Does not navigate anywhere itself,
  // so callers can send the user to different places afterwards (dashboard,
  // shortcuts, or straight into booking a future session).
  const leaveMatchingQueue = () => {
    clearRetryTimer();
    clearProviderHeartbeat();
    stopRing();
    matchingPausedRef.current = true;
    if (isProvider && providerInQueueRef.current && !matchedRef.current) {
      immediateOnDemandRealtime.leaveProviderQueue(criteria).catch(() => {});
      providerInQueueRef.current = false;
    }
    immediateOnDemandRealtime.disconnect();
  };

  const exitQueue = () => {
    leaveMatchingQueue();
    if (onExit) {
      onExit();
      return;
    }
    navigate(exitPath);
  };

  // Client-only: leave the queue and drop straight into booking a future
  // (donated/outreach) session, reusing the same "immediate-session-exit"
  // entry point appointment.js already listens for.
  const bookFutureSession = () => {
    leaveMatchingQueue();
    navigate("/appointment?from=immediate-session-exit&booking=donated");
  };

  const continueWaiting = () => {
    const nextWaitMinutes = isProvider
      ? parseWaitMinutes(nextWaitTime)
      : CLIENT_WAIT_MINUTES;

    if (!nextWaitMinutes) {
      toast.error(t("waiting.selectMinutesError"), {
        position: "top-center",
      });
      return;
    }

    matchingPausedRef.current = false;
    providerInQueueRef.current = false;
    setActiveWaitMinutes(nextWaitMinutes);
    setRemainingSeconds(nextWaitMinutes * 60);
    setWaitExpired(false);
    stopRing();
    setMessage(t("waiting.keepLooking", { count: nextWaitMinutes }));

    if (isProvider) {
      joinProviderQueue();
    } else {
      startClientRetry();
    }
  };
 const ringPlay = () => {
  const audio = audioRef.current;
  if (!audio) return;

  // pehle clear karo (duplicate na bane)
  if (audioIntervalRef.current) return;

  audioIntervalRef.current = setInterval(() => {
    audio.currentTime = 0;
    audio.volume = 0.25;
    audio.play().catch((err) => {
      console.log("Autoplay blocked:", err);
    });
  }, 2000); // har 2 sec me play
};

const stopRing = () => {
  if (audioIntervalRef.current) {
    clearInterval(audioIntervalRef.current);
    audioIntervalRef.current = null;
  }

  const audio = audioRef.current;
  if (audio) {
    audio.pause();
    audio.currentTime = 0;
  }
};
  const statusText = message || STATUS_LABELS[status] || STATUS_LABELS.connecting;
  const matchLabel = getMatchLabel(matchData, user && user.id);
  const showQueueTimer = activeWaitMinutes > 0 && status !== "matched";

  return (
    <>
    <audio ref={audioRef} src="/ring/dingNew.mp3" preload="auto" />
  
    <div className={`immediate-ondemand-page${embedded ? " immediate-ondemand-page--embedded" : ""}`}>
      <div className="immediate-ondemand-waiting">
        <div className="immediate-ondemand-copy">
          <h3>{title || t("waiting.title")}</h3>
          <p>
            {status === "matched" && matchLabel
              ? t("waiting.matchedWith", { name: matchLabel })
              : statusText}
          </p>
          {isProvider && settings && settings.waitTime ? (
            <span>{t("waiting.selectedWaitTime", { count: activeWaitMinutes || parseWaitMinutes(settings.waitTime) })}</span>
          ) : null}
        </div>

        {showQueueTimer ? (
          <div className="immediate-ondemand-timer">
            <span className="immediate-ondemand-timer-label">{isProvider ? t("waiting.timeRemaining") : t("waiting.timeRemainingSearch")}</span>
            <span className="immediate-ondemand-timer-value">{formatCountdown(remainingSeconds)}</span>
          </div>
        ) : null}

        {waitExpired && status !== "matched" ? (
          !isProvider && noMatchFound ? (
            <div className="immediate-ondemand-continue">
              <h4>{t("waiting.noMatch.title")}</h4>
              <p>{t("waiting.noMatch.prompt")}</p>
              <div className="immediate-ondemand-actions immediate-ondemand-actions--center">
                {showBookFutureSession &&(
                <button
                  type="button"
                  className="immediate-ondemand-secondary"
                  onClick={bookFutureSession}
                >
                  {t("waiting.bookFutureSession")}
                </button>
                )}
                <button
                  type="button"
                  className="immediate-ondemand-primary"
                  onClick={exitQueue}
                >
                  {t("waiting.tryAgainLater")}
                </button>
              </div>
            </div>
          ) : (
            <div className="immediate-ondemand-continue">
              <h4>{isProvider ? t("waiting.providerWaitEnded") : t("waiting.clientWaitEnded")}</h4>
              <p>
                {isProvider
                  ? t("waiting.providerContinuePrompt")
                  : t("waiting.clientContinuePrompt")}
              </p>
              {isProvider ? (
                <div className="immediate-ondemand-field">
                  <label htmlFor="immediate-next-wait-time">
                    {t("waiting.continueWaitingFor")}
                  </label>
                  <select
                    id="immediate-next-wait-time"
                    value={nextWaitTime}
                    onChange={(event) => setNextWaitTime(event.target.value)}
                  >
                    <option value="">{t("waiting.selectTime")}</option>
                    {waitOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="immediate-ondemand-actions immediate-ondemand-actions--center">
                <button
                  type="button"
                  className="immediate-ondemand-secondary"
                  onClick={!isProvider && showBookFutureSession ? bookFutureSession : exitQueue}
                >
                  {isProvider
                    ? t("waiting.bookingHub")
                    : showBookFutureSession
                    ? t("waiting.bookFutureSession")
                    : t("waiting.exitToDashboard")}
                </button>
                <button
                  type="button"
                  className="immediate-ondemand-primary"
                  onClick={continueWaiting}
                  disabled={isProvider && !nextWaitTime}
                >
                  {isProvider ? t("waiting.keepWaiting") : t("waiting.stayAnother")}
                </button>
              </div>
            </div>
          )
        ) : null}

        <div className="immediate-ondemand-globe">
          <img src="/localImages/AroundTheWorld.gif" alt={t("waiting.globeAlt")} />
        </div>

        {!waitExpired ? (
          <button
            type="button"
            className="immediate-ondemand-exit"
            onClick={() => setShowExitConfirm(true)}
            disabled={status === "matched"}
          >
            {t("waiting.exitQueue")}
          </button>
        ) : null}

        {showExitConfirm ? (
          <div className="immediate-ondemand-confirm-overlay">
            <div className="immediate-ondemand-confirm-card">
              <h4>
                {isProvider
                  ? t("waiting.exitConfirm.providerTitle")
                  : t("waiting.exitConfirm.clientTitle")}
              </h4>
              {!isProvider ? (
                <>
                  <p>
                    {showBookFutureSession
                      ? t("waiting.exitConfirm.loseWithFuture")
                      : t("waiting.exitConfirm.lose")}
                  </p>
                  <p className="immediate-ondemand-confirm-question">
                    {t("waiting.exitConfirm.question")}
                  </p>
                </>
              ) : null}
              <div className="immediate-ondemand-confirm-actions">
                {!isProvider ? (
                  <button
                    type="button"
                    className="immediate-ondemand-confirm-btn immediate-ondemand-confirm-btn--primary"
                    onClick={() => setShowExitConfirm(false)}
                  >
                    {t("waiting.keepWaiting")}
                  </button>
                ) : null}
                {!isProvider && showBookFutureSession ? (
                  <button
                    type="button"
                    className="immediate-ondemand-confirm-btn"
                    onClick={() => {
                      setShowExitConfirm(false);
                      bookFutureSession();
                    }}
                  >
                    {t("waiting.exitConfirm.bookFuture")}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="immediate-ondemand-confirm-btn"
                  onClick={() => {
                    setShowExitConfirm(false);
                    exitQueue();
                  }}
                >
                  {isProvider ? t("waiting.exitConfirm.yesExitBookingHub") : t("waiting.exitToDashboard")}
                </button>
                {isProvider ? (
                  <button
                    type="button"
                    className="immediate-ondemand-confirm-btn immediate-ondemand-confirm-btn--primary"
                    onClick={() => setShowExitConfirm(false)}
                  >
                    {t("waiting.keepWaiting")}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
      </>
  );
}
